import { Avatar, AvatarFallback, Badge, Checkbox } from '@folio/components';
import { cn, initialsOf } from '@folio/lib';
import { useQuery } from '@tanstack/react-query';
import type { EmailThreadDto } from 'korint-sdk';
import { emailThreadControllerGetThreadQueryOptions } from 'korint-sdk';
import { ChevronRight, Paperclip } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ArchiveButton } from './ArchiveButton';
import { ThreadEmails } from './ThreadEmails';
import { InboxDate } from './InboxDate';
import { counterpartOf, STRETCHED_BUTTON } from './threadHelpers';
import {
  AnalysisPending,
  LinkedPolicyBadges,
  MailboxIndicator,
  ToAttachBadge,
  usePolicyLabel,
} from './ThreadIndicators';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export function ThreadCard({
  thread,
  isSelected,
  selectedEmailId,
  isExpanded,
  isChecked,
  onToggle,
  onCheck,
  onSelectEmail,
  onSetArchived,
}: {
  thread: EmailThreadDto;
  isSelected: boolean;
  selectedEmailId: string | null;
  isExpanded: boolean;
  isChecked: boolean;
  onToggle: () => void;
  onCheck: (extendsRange: boolean) => void;
  onSelectEmail: (emailId: string) => void;
  onSetArchived: (archived: boolean) => void;
}) {
  const { t } = useTranslation();
  const { label: policyLabel } = usePolicyLabel();
  const { address: counterpartAddress, label: counterpartLabel } = counterpartOf(thread);
  // A single email opens straight in the reading pane: unfolding would repeat it
  const isFoldable = thread.emailCount > 1;
  // Unfolded: each email to attach carries its own badge instead of the thread's count
  const { data: threadDetail, isError: isThreadError } = useQuery({
    ...emailThreadControllerGetThreadQueryOptions(thread.id),
    enabled: isExpanded,
  });
  const emails = isExpanded ? threadDetail?.emails : undefined;
  const [firstPolicy, ...otherPolicies] = thread.linkedPolicies;
  const hasMailboxError = thread.account.status === 'error';

  return (
    <Stack asChild gap={1}>
      <li
        className={cn(
          'mx-2 my-1.5 rounded-md',
          isSelected && 'bg-primary/[0.05] ring-1 ring-inset ring-primary/20',
          isChecked && !isSelected && 'bg-muted',
        )}
        data-testid="general-inbox-thread"
        data-unread={thread.isUnread}
        aria-current={isSelected || undefined}
      >
        <Group
          align="stretch"
          className={cn(
            'group relative gap-1 rounded-md py-2 pl-0.5 pr-3 transition-colors',
            !isSelected && 'hover:bg-muted/70',
            thread.isArchived && 'text-muted-foreground',
          )}
        >
          <Checkbox
            checked={isChecked}
            aria-label={t('generalInbox.selection.select')}
            className="relative z-10 ml-1.5 mt-1.5"
            data-testid="general-inbox-thread-checkbox"
            onClick={event => onCheck(event.shiftKey)}
          />
          <ChevronRight
            aria-hidden
            className={cn(
              'mx-1 mt-1.5 size-4 shrink-0 text-muted-foreground transition-transform duration-150 ease-out',
              isExpanded && 'rotate-90',
              !isFoldable && 'invisible',
            )}
          />
          <Stack className="min-w-0 flex-1 [@media(hover:none)]:pr-6 gap-1.5">
            <button
              type="button"
              className={cn('block w-full space-y-1', STRETCHED_BUTTON)}
              aria-expanded={isFoldable ? isExpanded : undefined}
              onClick={onToggle}
            >
              <span className="flex items-center gap-2">
                <Avatar
                  aria-hidden
                  className={cn('size-6 shrink-0', thread.isArchived && 'opacity-50')}
                >
                  <AvatarFallback className="bg-sky-100 text-[10px] font-bold text-slate-700">
                    {initialsOf(counterpartLabel)}
                  </AvatarFallback>
                </Avatar>
                <Text
                  inline
                  size="sm"
                  truncate
                  weight={thread.isUnread ? 'bold' : 'medium'}
                  className="min-w-0 flex-1"
                  title={counterpartAddress}
                >
                  {counterpartLabel}
                </Text>
                {thread.isArchived && <span className="sr-only">{t('generalInbox.archived')}</span>}
                {thread.hasAttachments && (
                  <Paperclip
                    className="size-3.5 shrink-0 text-muted-foreground"
                    aria-label={t('generalInbox.hasAttachments')}
                  />
                )}
                <InboxDate
                  className={cn(
                    'shrink-0 text-xs tabular-nums transition-opacity group-focus-within:opacity-0 group-hover:opacity-0',
                    thread.isUnread ? 'font-semibold text-foreground' : 'text-muted-foreground',
                  )}
                  date={thread.date}
                />
              </span>

              <span className="flex items-center gap-2">
                <Text
                  inline
                  size="sm"
                  truncate
                  weight={thread.isUnread ? 'semibold' : undefined}
                  className="min-w-0 flex-1"
                >
                  {thread.subject || t('generalInbox.noSubject')}
                </Text>
                {thread.analysisPending ? (
                  <AnalysisPending />
                ) : (
                  !emails && <ToAttachBadge count={thread.toProcessCount} />
                )}
              </span>

              {/* Unfolded, each email row shows its own preview */}
              {thread.preview && !isExpanded && (
                <Text inline truncate size="xs" tone="muted" className="block">
                  {thread.preview}
                </Text>
              )}
            </button>

            {(hasMailboxError || firstPolicy) && (
              <Group wrap gap={0} className="gap-1.5">
                {hasMailboxError && (
                  // Above the stretched button for its tooltip, so it toggles the thread itself
                  <span className="relative z-10 min-w-0 max-w-full" onClick={onToggle}>
                    <MailboxIndicator account={thread.account} />
                  </span>
                )}
                {firstPolicy && <LinkedPolicyBadges policies={[firstPolicy]} />}
                {otherPolicies.length > 0 && (
                  <Badge
                    variant="outline"
                    className="bg-card"
                    title={otherPolicies.map(policyLabel).join(', ')}
                  >
                    +{otherPolicies.length}
                  </Badge>
                )}
              </Group>
            )}
          </Stack>
          {/* Takes the date's place on hover */}
          <ArchiveButton
            scope="thread"
            isArchived={thread.isArchived}
            className="absolute right-1.5 top-1 z-10"
            onClick={() => onSetArchived(!thread.isArchived)}
          />
        </Group>

        {isExpanded && (
          <div className="pb-1.5 pl-8 pr-1.5">
            <ThreadEmails
              emails={emails}
              isError={isThreadError}
              isAnalysisPending={thread.analysisPending}
              selectedEmailId={isSelected ? selectedEmailId : null}
              onSelectEmail={onSelectEmail}
            />
          </div>
        )}
      </li>
    </Stack>
  );
}
